import {getSavedData} from '#Common/savedData';
import {Stats} from '#Common/types';
import {UncapitalizeObjectKeys} from '#Server/util';

const ENVIRONMENT_BASE_URL_MAP: Record<string, string> = {
  development: `http://localhost:${__TEST_API__ ? 5003 : 5002}/word-crunch/api`,
  production: `https://chester.millisock.com/word-crunch/${
    __TEST_API__ ? 'api-test' : 'api'
  }`,
};

const BASE_URL = ENVIRONMENT_BASE_URL_MAP[process.env.NODE_ENV || 'production'];

const makeRequest = async (url: string, requestInit?: RequestInit) => {
  // TODO: try catch here? json() here?
  const headers = await createHeaders();
  return fetch(url, {...requestInit, headers});
};

const createHeaders = async (): Promise<Headers> => {
  const savedData = await getSavedData();
  const headerObject: HeadersInit = {
    'Content-Type': 'application/json',
    'cache-control': 'no-store',
  };
  const {userId} = savedData;
  if (userId) {
    headerObject.Authorization = `Basic ${userId}`;
  }
  headerObject.clientappversion = __VERSION__;
  return new Headers(headerObject);
};

type PuzzleData = {
  puzzleId: string;
  puzzleNumber: number;
  fixedCells: Record<string, string>;
  lettersRandom: string[];
  date: string;
  secondsUntilNewPuzzles: number;
  gameStats: number[];
  userId: string;
};

type CurrentDateStringResponse = {
  dateString: string;
};
export const getCurrentDateString =
  async (): Promise<CurrentDateStringResponse> => {
    return (await makeRequest(`${BASE_URL}/v1/puzzle/date`)).json();
  };

export const getPuzzle = async (difficulty: number): Promise<PuzzleData> => {
  return (
    await makeRequest(`${BASE_URL}/v1/puzzle/wc_5_c/${difficulty}`)
  ).json();
};

type ValidateResponse = {
  success: boolean;
  victory: boolean;
  validation: string[];
  userId: string;
  stats: Stats;
};
export const validate = async (
  attempt: string,
  puzzleId: string,
): Promise<ValidateResponse> => {
  return (
    await makeRequest(
      `${BASE_URL}/v1/puzzle/wc_5_c/${puzzleId}/validate/${attempt}`,
    )
  ).json();
};

export type PuzzleSolution = {
  Id: string;
  Hints: Record<string, string>;
  Word1: string;
  Word2: string;
  Word3: string;
  Word4: string;
  Word5: string;
  Date: string;
};

export type SolutionsResponse = {
  puzzleEasy: PuzzleSolution;
  puzzleMedium: PuzzleSolution;
  puzzleHard: PuzzleSolution;
  puzzleBrutal: PuzzleSolution;
};
export const getYesterdaySolutions = async (): Promise<SolutionsResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/puzzle/wc_5_c/solutions/yesterday`)
  ).json();
};

export const getMyScores = async () => {
  return (await makeRequest(`${BASE_URL}/v1/scores/my`)).json();
};

export type LeaderboardResult = {
  rank: number;
  name: string;
  score: number;
  isMe: boolean;
};

export type LeaderboardResponse = {
  todayResults: LeaderboardResult[];
  thisWeekResults: LeaderboardResult[];
  lastWeekResults: LeaderboardResult[];
  dateString: string;
  firstDayOfWeekString: string;
  lastDayOfWeekString: string;
  firstDayOfLastWeekString: string;
  lastDayOfLastWeekString: string;
};

export const getLeaderboards = async (): Promise<LeaderboardResponse> => {
  return (await makeRequest(`${BASE_URL}/v1/scores/leaderboards`)).json();
};

type UpdateUserNameResponse = {
  success: boolean;
  name: string;
};

export const updateUserName = async (
  name: string,
): Promise<UpdateUserNameResponse> => {
  const data = {
    name,
  };
  return (
    await makeRequest(`${BASE_URL}/v1/users/me`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  ).json();
};

type PostPaymentIntentResponse =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      id: string;
      clientSecret: string;
    };

export const postPaymentIntent =
  async (): Promise<PostPaymentIntentResponse> => {
    return (
      await makeRequest(`${BASE_URL}/v1/payments/intents`, {
        method: 'POST',
      })
    ).json();
  };

export const deletePaymentIntent = async (
  id: string,
): Promise<PostPaymentIntentResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/payments/intents/${id}`, {
      method: 'DELETE',
    })
  ).json();
};

export type ProductResultDb = {
  Id: number;
  Name: string;
  Description: string;
  Price: number;
};

export type ShopProduct = UncapitalizeObjectKeys<ProductResultDb>;

type GetShopProductsResponse = {
  success: boolean;
  shopProducts: ShopProduct[];
  basket: Record<number, number>;
  purchasedProducts: Record<number, number>;
};

export const getShopProducts = async (): Promise<GetShopProductsResponse> => {
  return (await makeRequest(`${BASE_URL}/v1/payments/shopProducts`)).json();
};

type PostBasketProductsResponse = {
  success: boolean;
  basket: Record<number, number>;
};
export const postBasketProduct = async (
  productId: number,
): Promise<PostBasketProductsResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/payments/basketProducts`, {
      method: 'POST',
      body: JSON.stringify({
        productId,
      }),
    })
  ).json();
};

type DeleteBasketProductsResponse = {
  success: boolean;
  basket: Record<number, number>;
};
export const deleteBasketProduct = async (
  productId: number,
): Promise<DeleteBasketProductsResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/payments/basketProducts`, {
      method: 'DELETE',
      body: JSON.stringify({
        productId,
      }),
    })
  ).json();
};

export type GetOrderResponse = {
  success: boolean;
  orderId: number;
  orderKey: string;
  amount: number;
  createdAt: string;
  products: ShopProduct[];
};
export const getOrder = async (
  orderPaymentIntentClientSecret: string,
): Promise<GetOrderResponse> => {
  return (
    await makeRequest(
      `${BASE_URL}/v1/payments/orders/paymentIntentClientSecret/${orderPaymentIntentClientSecret}`,
    )
  ).json();
};

export type GetPuzzlesWithWordResponse = {
  dailyPuzzles: {Id: number; Difficulty: number; DateString: string}[];
  isWordInTodayPuzzles: boolean;
};
export const getPuzzlesWithWord = async (
  word: string,
): Promise<GetPuzzlesWithWordResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/puzzleadmin/puzzlesWithWord/${word}`)
  ).json();
};

export type InvalidateWordResponse = {
  inactivePuzzleIds: number[];
  replacements: {difficulty: number; dateString: string}[];
};

export const invalidateWord = async (
  word: string,
  includeToday: boolean,
): Promise<InvalidateWordResponse> => {
  return (
    await makeRequest(`${BASE_URL}/v1/puzzleadmin/invalidateWord/${word}`, {
      method: 'POST',
      body: JSON.stringify({
        includeToday,
      }),
    })
  ).json();
};

export const getTodaySolutions = async (): Promise<SolutionsResponse> => {
  return (await makeRequest(`${BASE_URL}/v1/puzzleadmin/solutions`)).json();
};
